import React, { useState, useEffect, useRef } from 'react';
import { collection, query, where, orderBy, getDocs, deleteDoc, doc, updateDoc, getDoc, limit } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useTranslation } from '../hooks/useTranslation';
import { useNavigate } from 'react-router-dom';


const Monitoring = () => {
  const [monitoredSearches, setMonitoredSearches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchCadence, setSearchCadence] = useState('6months');
  const [savingCadence, setSavingCadence] = useState(false);
  const [filterText, setFilterText] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const { currentUser, currentOrg } = useAuth();
  const { showSuccess, showError } = useToast();
  const t = useTranslation();
  const fetchAttempted = useRef(false);
  const navigate = useNavigate();
  
  // Load monitored searches and user's search cadence preference on component mount
  useEffect(() => {
    const fetchUserData = async () => {
      if (!currentUser || !currentOrg || fetchAttempted.current) return;
      
      try {
        setLoading(true);
        setError(null);
        fetchAttempted.current = true;
        
        // Fetch the user's search cadence preference
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (userDoc.exists() && userDoc.data().searchCadence) {
          setSearchCadence(userDoc.data().searchCadence);
        }
        
        // Fetch monitored searches
        await fetchMonitoredSearches();
        
      } catch (error) {
        console.error('Error fetching user data:', error);
        setError(error.message || t('monitoring.fetchError'));
        showError(t('monitoring.fetchError'));
      } finally {
        setLoading(false);
      }
    };
    
    fetchUserData();
  }, [currentUser, currentOrg, showError, t]);
  
  // Effect to perform search when filter text changes
  useEffect(() => {
    if (!currentUser || !currentOrg || !fetchAttempted.current) return;
    
    if (filterText.trim() === '') {
      // If filter is cleared, fetch all monitored searches
      fetchMonitoredSearches();
      return;
    }
    
    // Debounce search to avoid too many queries
    const debounceTimer = setTimeout(() => {
      performSearch(filterText);
    }, 500);
    
    return () => clearTimeout(debounceTimer);
  }, [filterText, currentUser, currentOrg]);
  
  // Fetch all monitored searches
  const fetchMonitoredSearches = async () => {
    if (!currentUser || !currentOrg) return;
    
    try {
      setLoading(true);
      
      const q = query(
        collection(db, 'monitoring'),
        where('orgId', '==', currentOrg.id),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      
      const searches = [];
      querySnapshot.forEach((doc) => {
        searches.push({
          id: doc.id,
          ...doc.data(),
          // Format timestamps if they exist
          createdAt: doc.data().createdAt ? 
            new Date(doc.data().createdAt.toDate()).toLocaleString() : 
            `Unknown (${Date.now()})`, // Fallback with unique value
          lastSearchDate: doc.data().lastSearchDate ? 
            new Date(doc.data().lastSearchDate.toDate()).toLocaleString() : 
            `Unknown (${Date.now()})` // Fallback with unique value
        });
      });
      
      setMonitoredSearches(searches);
    } catch (error) {
      console.error('Error fetching monitored searches:', error);
      setError(error.message || t('monitoring.fetchError'));
      showError(t('monitoring.fetchError'));
    } finally {
      setLoading(false);
    }
  };
  
  // Function to search through all monitored searches
  const performSearch = async (searchTerm) => {
    if (!searchTerm.trim() || !currentUser || !currentOrg) return;
    
    try {
      setIsSearching(true);
      
      // Create a reference to the monitoring collection
      const monitoringRef = collection(db, 'monitoring');
      
      // Create a query for the current organization's monitored searches
      // Fetch all results since we expect the list to be manageable
      const monitoringQuery = query(
        monitoringRef,
        where('orgId', '==', currentOrg.id),
        orderBy('createdAt', 'desc')
      );
      
      // Execute the query
      const querySnapshot = await getDocs(monitoringQuery);
      
      // Filter results client-side by searchTerm
      const filteredResults = querySnapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt ? 
            new Date(doc.data().createdAt.toDate()).toLocaleString() : 
            `Unknown (${Date.now()})`,
          lastSearchDate: doc.data().lastSearchDate ? 
            new Date(doc.data().lastSearchDate.toDate()).toLocaleString() : 
            `Unknown (${Date.now()})`
        }))
        .filter(item => 
          item.searchTerm && 
          item.searchTerm.toLowerCase().includes(searchTerm.toLowerCase())
        );
      
      // Update state
      setMonitoredSearches(filteredResults);
      
    } catch (err) {
      console.error('Error searching monitored searches:', err);
      setError(err.message || t('monitoring.searchError'));
    } finally {
      setIsSearching(false);
    }
  };
  
  // Save search cadence to user profile
  const handleCadenceChange = async (e) => {
    if (!currentUser) return;
    
    const newCadence = e.target.value;
    setSearchCadence(newCadence);
    
    try {
      setSavingCadence(true);
      await updateDoc(doc(db, 'users', currentUser.uid), {
        searchCadence: newCadence,
        updatedAt: new Date()
      });
      showSuccess(t('monitoring.cadenceSaved'));
    } catch (error) {
      console.error('Error saving search cadence:', error);
      showError(t('monitoring.saveCadenceError'));
    } finally {
      setSavingCadence(false);
    }
  };
  
  // Handle retry when index is building
  const handleRetry = () => {
    fetchAttempted.current = false;
    setLoading(true);
    setError(null);
    setFilterText(''); // Clear any search filter
    
    // Wait a bit before retrying to avoid hammering the server
    setTimeout(() => {
      const fetchUserData = async () => {
        if (!currentUser) return;
        
        try {
          // Fetch the user's search cadence preference
          const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
          if (userDoc.exists() && userDoc.data().searchCadence) {
            setSearchCadence(userDoc.data().searchCadence);
          }
          
          // Fetch monitored searches
          await fetchMonitoredSearches();
          setError(null);
        } catch (error) {
          console.error('Error retrying user data fetch:', error);
          setError(error.message || t('monitoring.fetchError'));
          showError(t('monitoring.fetchError'));
        } finally {
          setLoading(false);
        }
      };
      
      fetchUserData();
    }, 2000);
  };
  
  // Handle removing from monitoring
  const handleRemoveFromMonitoring = async (id) => {
    if (!currentUser) return;
    
    try {
      await deleteDoc(doc(db, 'monitoring', id));
      
      // Update state to remove the deleted item
      setMonitoredSearches(prevSearches => 
        prevSearches.filter(search => search.id !== id)
      );
      
      showSuccess(t('monitoring.removedSuccess'));
    } catch (error) {
      console.error('Error removing from monitoring:', error);
      showError(t('monitoring.removeError'));
    }
  };
  
  // Run new search for a monitored item
  const handleRunSearch = (searchTerm) => {
    // Navigate to search page with the search term and source info
    navigate(`/?q=${encodeURIComponent(searchTerm)}`, { state: { from: 'monitoring' } });
  };
  
  // Render error state
  if (error) {
    return (
      <div className="max-w-4xl mx-auto p-6 bg-white dark:bg-gray-800 rounded-lg shadow-md">
        <div className="border-b pb-4 mb-6 border-gray-200 dark:border-gray-700">
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white">{t('monitoring.title')}</h1>
        </div>
        
        <div className="flex flex-col items-center justify-center p-8 text-center bg-red-50 dark:bg-red-900/20 rounded-lg">
          <i className="fa-solid fa-triangle-exclamation text-red-500 dark:text-red-400 text-3xl mb-4"></i>
          <h3 className="text-xl font-semibold text-red-700 dark:text-red-300 mb-2">{t('monitoring.errorTitle')}</h3>
          <p className="text-red-600 dark:text-red-200 mb-6">
            {error.includes('index') 
              ? t('monitoring.indexBuildingError') 
              : t('monitoring.fetchError')}
          </p>
          <button 
            className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-md transition-colors flex items-center gap-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
            onClick={handleRetry}
          >
            <i className="fa-solid fa-rotate"></i>
            {t('common.retry')}
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="max-w-4xl mx-auto p-6 bg-white dark:bg-gray-800 rounded-lg shadow-md">
      <div className="border-b pb-4 mb-6 border-gray-200 dark:border-gray-700">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-white">{t('monitoring.title')}</h1>
        <p className="text-gray-600 dark:text-gray-300 mt-2">{t('monitoring.description')}</p>
      </div>
      
      <div className="bg-gray-50 dark:bg-gray-700/30 rounded-lg p-4 mb-6">
        <div className="mb-4">
          <label htmlFor="search-cadence" className="block font-medium text-gray-700 dark:text-gray-200 mb-2">
            {t('monitoring.searchCadenceLabel')}:
          </label>
          <div className="relative inline-block w-full max-w-xs">
            <select 
              id="search-cadence" 
              value={searchCadence} 
              onChange={handleCadenceChange}
              disabled={loading || savingCadence}
              className="block w-full pl-3 pr-10 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-gray-700 dark:text-white disabled:opacity-50"
            >
              <option value="3months">{t('monitoring.cadence3Months')}</option>
              <option value="6months">{t('monitoring.cadence6Months')}</option>
              <option value="9months">{t('monitoring.cadence9Months')}</option>
              <option value="1year">{t('monitoring.cadence1Year')}</option>
              <option value="2years">{t('monitoring.cadence2Years')}</option>
            </select>
            {savingCadence && (
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-blue-500 dark:text-blue-400">
                <i className="fa-solid fa-circle-notch fa-spin"></i>
              </span>
            )}
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">{t('monitoring.cadenceDescription')}</p>
        </div>
      </div>
      
      {loading ? (
        <div className="flex justify-center items-center p-10 text-gray-600 dark:text-gray-300">
          <i className="fa-solid fa-circle-notch fa-spin text-xl mr-3"></i>
          <span className="text-lg">{t('common.loading')}</span>
        </div>
      ) : monitoredSearches.length === 0 && !isSearching ? (
        <div className="flex flex-col items-center justify-center p-10 text-center bg-gray-50 dark:bg-gray-700/30 rounded-lg">
          <i className="fa-solid fa-shield text-4xl text-gray-400 dark:text-gray-500 mb-4"></i>
          <h3 className="text-xl font-semibold text-gray-700 dark:text-gray-200 mb-2">{t('monitoring.noMonitoredSearches')}</h3>
          <p className="text-gray-600 dark:text-gray-300">{t('monitoring.emptyMessage')}</p>
        </div>
      ) : (
        <>
          <div className="mb-4 text-gray-700 dark:text-gray-200 font-medium">
            {t('monitoring.totalMonitored')}: <span className="text-blue-600 dark:text-blue-400">{monitoredSearches.length}</span>
          </div>
          
          <div className="mb-6">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <i className="fa-solid fa-magnifying-glass text-gray-400 dark:text-gray-500"></i>
              </div>
              <input 
                type="text"
                placeholder={t('monitoring.filterMonitored')}
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                className="block w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md py-2 pl-10 pr-10 focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-gray-700 dark:text-white"
              />
              {filterText && (
                <button 
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
                  onClick={() => setFilterText('')}
                  aria-label="Clear filter"
                >
                  <span className="text-xl font-medium">×</span>
                </button>
              )}
            </div>
            {isSearching && (
              <div className="flex items-center mt-2 text-sm text-gray-500 dark:text-gray-400">
                <div className="w-3 h-3 mr-2 rounded-full border-2 border-t-transparent border-blue-500 animate-spin"></div>
                <span>{t('monitoring.searching')}</span>
              </div>
            )}
          </div>
          
          <div className="space-y-4">
            {monitoredSearches.length === 0 ? (
              <div className="flex flex-col items-center justify-center p-8 text-center bg-gray-50 dark:bg-gray-700/30 rounded-lg">
                <i className="fa-solid fa-search text-3xl text-gray-400 dark:text-gray-500 mb-3"></i>
                <p className="text-gray-600 dark:text-gray-300">{t('monitoring.noMatchingSearches')}</p>
              </div>
            ) : (
              monitoredSearches.map((search) => (
                <div key={search.id} className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg shadow-sm overflow-hidden">
                  <div className="p-4">
                    <div className="text-lg font-medium text-gray-800 dark:text-white mb-3">{search.searchTerm}</div>
                    <div className="grid md:grid-cols-3 gap-3 text-sm">
                      <div className="text-gray-700 dark:text-gray-200">
                        <span className="font-medium text-gray-500 dark:text-gray-400">{t('monitoring.verificationId')}:</span>{' '}
                        <span>{search.verificationId}</span>
                      </div>
                      <div className="text-gray-700 dark:text-gray-200">
                        <span className="font-medium text-gray-500 dark:text-gray-400">{t('monitoring.addedOn')}:</span>{' '}
                        <span>{search.createdAt}</span>
                      </div>
                      <div className="text-gray-700 dark:text-gray-200">
                        <span className="font-medium text-gray-500 dark:text-gray-400">{t('monitoring.lastChecked')}:</span>{' '}
                        <span>{search.lastSearchDate}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex flex-wrap border-t border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-600">
                    <button 
                      className="flex-1 py-3 px-4 flex items-center justify-center gap-2 text-sm font-medium text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20 transition-colors"
                      onClick={() => handleRunSearch(search.searchTerm)}
                      title={t('monitoring.runSearch')}
                    >
                      <i className="fa-solid fa-magnifying-glass"></i>
                      {t('monitoring.runSearch')}
                    </button>
                    <button 
                      className="flex-1 py-3 px-4 flex items-center justify-center gap-2 text-sm font-medium text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20 transition-colors border-l border-gray-200 dark:border-gray-600"
                      onClick={() => handleRemoveFromMonitoring(search.id)}
                      title={t('monitoring.removeFromMonitoring')}
                    >
                      <i className="fa-solid fa-eye-slash"></i>
                      {t('monitoring.removeFromMonitoring')}
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default Monitoring; 